import {
  FilterClause,
  FilterDefinition,
  FilterOperator,
  FilterType,
} from './schema';
import {
  MARKETPLACE_PROJECTS_DEFAULT_FILTERS,
  MARKETPLACE_PROJECTS_DATA_LANDSCAPE,
  MARKETPLACE_PROJECTS_ENVIRONMENT,
  MARKETPLACE_PROJECTS_SYSTEM_URN,
  MARKETPLACE_PROJECTS_URN,
} from '@agilelab/plugin-wb-search-common';
import { SearchFilterConfig } from '@agilelab/plugin-wb-search-common';
import { err, ok, parseUrn, Result } from '@agilelab/plugin-wb-platform-common';

const operatorMatrix: Record<FilterType, FilterOperator[]> = {
  text: ['equals', 'startsWith', 'endsWith', 'contains'],
  choice: ['in', 'notIn', 'inIgnoreCase', 'notInIgnoreCase'],
  domain: ['in', 'notIn', 'inIgnoreCase', 'notInIgnoreCase'],
  type: ['in', 'notIn', 'inIgnoreCase', 'notInIgnoreCase'],
  boolean: ['equals'],
  date: ['onOrAfter', 'onOrBefore'],
};

const hiddenFields = new Set([
  MARKETPLACE_PROJECTS_ENVIRONMENT.field,
  MARKETPLACE_PROJECTS_SYSTEM_URN.field,
  '_computedInfo.consumable',
  'id',
]);

const typeMap: Partial<Record<SearchFilterConfig['type'], FilterType>> = {
  text: 'text',
  choice: 'choice',
  domain: 'domain',
  type: 'type',
  boolean: 'boolean',
  date: 'date',
};

/**
 * Maps internal Marketplace filter configuration to the caller-independent
 * public vocabulary. Private fields are removed, defaults remain client hints,
 * and `urn` is the only non-facetable definition.
 */
export function mapFilterDefinitions(
  configuredFilters: SearchFilterConfig[],
): FilterDefinition[] {
  const configured = new Map<string, SearchFilterConfig>(
    configuredFilters.map(filter => [filter.field, filter]),
  );
  const source: SearchFilterConfig[] = [
    ...MARKETPLACE_PROJECTS_DEFAULT_FILTERS,
    ...configuredFilters.filter(
      filter =>
        !MARKETPLACE_PROJECTS_DEFAULT_FILTERS.some(
          defaultFilter => defaultFilter.field === filter.field,
        ),
    ),
  ];
  const seen = new Set<string>();

  return source.flatMap(filter => {
    if (seen.has(filter.field) || hiddenFields.has(filter.field)) return [];
    seen.add(filter.field);

    const type =
      filter.field === MARKETPLACE_PROJECTS_URN.field
        ? 'choice'
        : typeMap[filter.type];
    if (!type) return [];
    const operators =
      filter.field === MARKETPLACE_PROJECTS_URN.field
        ? (['in'] as FilterOperator[])
        : operatorMatrix[type];
    const definition: FilterDefinition = {
      id: filter.field,
      name: filter.displayLabel ?? filter.label,
      type,
      operators: [...operators],
      facetable: filter.field !== MARKETPLACE_PROJECTS_URN.field,
    };
    const effective: SearchFilterConfig =
      configured.get(filter.field) ?? filter;
    if (effective.defaultValues?.length)
      definition.defaultValues = [...effective.defaultValues];
    if (effective.landscapes?.length) {
      definition.applicableDataLandscapes = [...effective.landscapes];
    }
    return [definition];
  });
}

/**
 * Validates filter identifiers/operators against discovery and canonicalizes
 * set values for stable downstream execution and cursor binding.
 */
export function validateFilterClauses(
  clauses: FilterClause[],
  definitions: FilterDefinition[],
): FilterClause[] | undefined {
  const result = normalizeFilterClauses(clauses, definitions);
  return result.ok ? result.value : undefined;
}

export type FilterNormalizationError = {
  code: 'invalid_filter';
  message: string;
};

function invalidFilter(message: string): FilterNormalizationError {
  return { code: 'invalid_filter', message };
}

function isValidDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(value);
  if (!match) return false;
  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return (
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth &&
    !Number.isNaN(Date.parse(value))
  );
}

function validateValue(
  definition: FilterDefinition,
  value: string | boolean,
): boolean {
  if (definition.type === 'boolean') return typeof value === 'boolean';
  if (typeof value !== 'string') return false;
  if (definition.type === 'date') return isValidDate(value);
  if (definition.id === MARKETPLACE_PROJECTS_URN.field) {
    try {
      parseUrn(value);
    } catch {
      return false;
    }
  }
  return true;
}

function canonicalizeValues(
  values: Array<string | boolean>,
  ignoreCase: boolean,
): Array<string | boolean> {
  const unique = new Map(
    values.map(value => {
      const canonical =
        typeof value === 'string' && ignoreCase
          ? value.toLocaleLowerCase('en-US')
          : value;
      return [`${typeof canonical}:${String(canonical)}`, canonical];
    }),
  );
  return [...unique.values()].sort((left, right) => {
    const leftValue = String(left);
    const rightValue = String(right);
    if (leftValue < rightValue) return -1;
    if (leftValue > rightValue) return 1;
    return 0;
  });
}

/** Validates public clauses and returns one canonical request representation. */
export function normalizeFilterClauses(
  clauses: FilterClause[],
  definitions: FilterDefinition[],
): Result<FilterClause[], FilterNormalizationError> {
  if (clauses.length > 20) {
    return err(invalidFilter('A request may contain at most 20 filters'));
  }
  const definitionMap = new Map(
    definitions.map(definition => [definition.id, definition]),
  );
  const normalized: FilterClause[] = [];

  for (const clause of clauses) {
    const definition = definitionMap.get(clause.id);
    if (!definition || !definition.operators.includes(clause.operator)) {
      return err(invalidFilter('The filter or operator is not advertised'));
    }
    if (
      !['in', 'notIn', 'inIgnoreCase', 'notInIgnoreCase'].includes(
        clause.operator,
      ) &&
      clause.values.length !== 1
    ) {
      return err(
        invalidFilter('This filter operator requires exactly one value'),
      );
    }
    if (!clause.values.every(value => validateValue(definition, value))) {
      return err(invalidFilter('A filter value has an invalid type or format'));
    }
    normalized.push({
      ...clause,
      values: canonicalizeValues(
        clause.values,
        clause.operator === 'inIgnoreCase' ||
          clause.operator === 'notInIgnoreCase',
      ),
    });
  }

  const selectedDataLandscapes = normalized
    .filter(
      clause =>
        clause.id === MARKETPLACE_PROJECTS_DATA_LANDSCAPE.field &&
        (clause.operator === 'in' || clause.operator === 'inIgnoreCase'),
    )
    .flatMap(clause =>
      clause.values.map(value => ({
        value: String(value),
        ignoreCase: clause.operator === 'inIgnoreCase',
      })),
    );
  for (const clause of normalized) {
    const applicable = definitionMap.get(clause.id)?.applicableDataLandscapes;
    if (
      applicable?.length &&
      (selectedDataLandscapes.length === 0 ||
        selectedDataLandscapes.some(
          selected =>
            !applicable.some(value =>
              selected.ignoreCase
                ? value.toLocaleLowerCase('en-US') === selected.value
                : value === selected.value,
            ),
        ))
    ) {
      return err(
        invalidFilter(
          'The filter is not applicable to the selected Data Landscape',
        ),
      );
    }
  }

  normalized.sort(
    (left, right) =>
      left.id.localeCompare(right.id) ||
      left.operator.localeCompare(right.operator) ||
      JSON.stringify(left.values).localeCompare(JSON.stringify(right.values)),
  );
  return ok(normalized);
}

export function normalizeSearchQuery(query: string): string {
  return query.trim();
}

export { operatorMatrix };
